import {
  Fragment,
  type ClipboardEvent,
  type DragEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'

import { FileText, Folder, Globe, Image as ImageIcon, Link2, Mic, Paperclip } from 'lucide-react'

import { cn } from '../../components/cn'
import {
  uploadMemoAttachment,
  useCreateMemo,
  useCreateVoiceMemo,
  type MemoScope,
} from '../../app/queries'
import { enqueueAndNotify } from '../../app/memo-queue'
import { t } from '../../copy'
import { T } from '../../copy/text'

/**
 * 快速记录框 `CaptureBox`（设计稿 SPEC-components §2，S01/S13/S14/S15）。
 *
 * 结构从上到下：作用域行（高 34px，图标+文字+备注+「改成杂项」）→
 * 正文区 → 附件条（选中的文件排队，上传中显示真实字节进度）→
 * 工具行（录一段 / 附件 / 链接 + 提示 + 保存）→ 失败时的状态条。
 *
 * ## 工具行按设计稿 S01/S06
 *
 * [录一段][附件][链接]。没有「AI 可读」开关——设计稿的输入框里没有它，
 * 新建一律「尚未允许」（SPEC-scope-ai §2.3 不给默认开的便利），
 * 语音路径强制选一次（服务端同样强制），文字路径要开去详情里开。
 *
 * ## 附件（SPEC-scope-ai §7 / SPEC-components §8）
 *
 * 选文件时先拦类型与大小；保存是两段：正文先建条目，附件逐个上传、
 * 各自一个状态（等正文 → 上传中（真实字节百分比）→ 传上去了 / 没传上去）。
 * **有附件没传上去，这个框就不清空**——重试或移除都处理完才收摊，
 * 否则用户会以为整条都存好了。列表与详情只渲染查得到的附件（= done）。
 *
 * ## 链接（设计稿「粘贴链接会自动识别」）
 *
 * 链接写进正文（「链接 —— 备注」的格式来自设计稿 SEED），列表按
 * 「链接」来源显示——识别在展示侧做，不另存一份链接字段。
 *
 * ## 键盘（S13）
 *
 * Enter 保存 / Shift+Enter 换行 / **输入法组合期（isComposing）不提交**。
 */
export interface CaptureBoxProps {
  scope: MemoScope & { projectName?: string }
  /** 浮层版（按 N）：正文区高一点，作用域行不变。 */
  variant?: 'inline' | 'floating'
  placeholder?: string
  /** 全部存好（正文 + 附件都落定）后通知外层——快记浮层在这一刻才关。 */
  onSaved?: () => void
  'data-testid'?: string
}

/** 外层（写一条 / 空态按钮）要能聚焦正文、直接开录音。 */
export interface CaptureBoxHandle {
  focus: () => void
  openVoice: () => void
}

/** 附件在框里的四态。waiting 是「选好了、等正文先存」。 */
type Clip =
  | { kind: 'waiting'; key: string; file: File }
  | { kind: 'uploading'; key: string; file: File; percent: number | null }
  | { kind: 'done'; key: string; file: File }
  | { kind: 'failed'; key: string; file: File; reason: string }

/** 类型白名单与大小上限，与服务端（memo 包 + 库 CHECK）同一张单子。 */
const CLIP_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,application/json,application/zip'
const CLIP_TYPES = new Set(CLIP_ACCEPT.split(','))
const CLIP_MAX_BYTES = 10 << 20

export const CaptureBox = forwardRef<CaptureBoxHandle, CaptureBoxProps>(function CaptureBox(
  props,
  ref,
) {
  const { scope } = props
  const floating = props.variant === 'floating'
  // 项目内可显式「改成杂项」。不做项目切换器（设计稿硬约束 01）。
  const [overrideMisc, setOverrideMisc] = useState(false)
  const effectiveScope: MemoScope = {
    projectId: overrideMisc ? undefined : scope.projectId,
  }

  const draftKey = `memo-draft:${effectiveScope.projectId ?? 'global'}`
  const [body, setBody] = useState(() => {
    try {
      return window.localStorage.getItem(draftKey) ?? ''
    } catch {
      return ''
    }
  })
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'failed'>('idle')
  const [failReason, setFailReason] = useState<{ reason: string; time: string } | null>(null)
  const [rejectNotice, setRejectNotice] = useState<string | null>(null)
  const [clips, setClips] = useState<Clip[]>([])
  /**
   * clips 的镜像。上传是并发的：最后一个附件的 setClips(done) 与
   * 「都传完了吗」的判定在同一个 tick 里，state 那份要等下一次渲染
   * 才看得见——判收摊条件必须读同步的这一份。
   */
  const clipsRef = useRef<Clip[]>([])
  /** 正文已经存好的那条（附件往它身上传）。存好到清空之间它非空。 */
  const createdMemoId = useRef<string | null>(null)

  /** clips 的唯一写口：ref 与 state 同步走，收摊判定才不会读到旧账。 */
  function updateClips(fn: (prev: Clip[]) => Clip[]) {
    clipsRef.current = fn(clipsRef.current)
    setClips(clipsRef.current)
  }

  // 语音入口的开合抬到这一层：空态的「录一段」按钮要能从外面打开它。
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    openVoice: () => {
      setVoiceOpen(true)
    },
  }))

  const create = useCreateMemo(effectiveScope)

  // 草稿落本机：失败重试的底牌，也是「关了浏览器再回来还在」的那份。
  // 附件不进草稿——File 对象存不进 localStorage，如实在附件条里标状态。
  useEffect(() => {
    try {
      if (body.trim()) window.localStorage.setItem(draftKey, body)
      else window.localStorage.removeItem(draftKey)
    } catch {
      // 隐私模式等场景 localStorage 会抛——草稿是承诺不是义务，吞掉
    }
  }, [body, draftKey])

  // 在传的才算忙。waiting 是「等正文先存」——它正是要等保存才动，
  // 算进忙会把保存按钮永久禁用（排队永远等不到出发）。
  const clipsBusy = clips.some((c) => c.kind === 'uploading')
  const clipsFailed = clips.filter((c): c is Extract<Clip, { kind: 'failed' }> => c.kind === 'failed')

  /** 存好正文后把排队的附件逐个传上去。全部落定（无一失败）才收摊。 */
  async function uploadQueued(memoId: string) {
    const queued = clipsRef.current.filter((c) => c.kind === 'waiting' || c.kind === 'failed')
    if (queued.length === 0) {
      finishIfSettled()
      return
    }
    updateClips((prev) =>
      prev.map((c) =>
        c.kind === 'waiting' || c.kind === 'failed'
          ? { kind: 'uploading', key: c.key, file: c.file, percent: null }
          : c,
      ),
    )
    await Promise.allSettled(
      queued.map(async (clip) => {
        try {
          await uploadMemoAttachment({
            memoId,
            file: clip.file,
            onProgress: (percent) => {
              updateClips((prev) =>
                prev.map((c) =>
                  c.key === clip.key && c.kind === 'uploading' ? { ...c, percent } : c,
                ),
              )
            },
          })
          updateClips((prev) =>
            prev.map((c) => (c.key === clip.key ? { kind: 'done', key: c.key, file: c.file } : c)),
          )
        } catch (error) {
          updateClips((prev) =>
            prev.map((c) =>
              c.key === clip.key
                ? {
                    kind: 'failed',
                    key: c.key,
                    file: c.file,
                    reason: error instanceof Error ? error.message : '',
                  }
                : c,
            ),
          )
        }
      }),
    )
    finishIfSettled()
  }

  /** 收摊条件：正文存好，且没有等传/在传/失败的附件。 */
  function finishIfSettled() {
    if (clipsRef.current.some((c) => c.kind !== 'done')) return
    if (!createdMemoId.current) return
    // 全部落定：清空回初始态。正文那条已经在列表里了。
    createdMemoId.current = null
    clipsRef.current = []
    setClips([])
    setSaveState('idle')
    setFailReason(null)
    setBody('')
    setRejectNotice(null)
    props.onSaved?.()
  }

  async function commit() {
    if (!body.trim() || saveState === 'saving' || clipsBusy) return
    setSaveState('saving')
    setRejectNotice(null)
    try {
      const created = await create.mutateAsync({ body: body.trim() })
      createdMemoId.current = created.id
      setSaveState('idle')
      setFailReason(null)
      await uploadQueued(created.id)
    } catch (error) {
      // 断网（R2-4，S05 的「等上传」）：正文和附件整条进本机队列，
      // 列表里带「等上传」标记，网络回来自动补传——不是失败，别给红条
      if (!navigator.onLine) {
        await enqueueAndNotify({
          id: (crypto.randomUUID?.() ?? `clip-${Date.now()}-${Math.random().toString(36).slice(2)}`),
          projectId: effectiveScope.projectId ?? null,
          body: body.trim(),
          files: clipsRef.current.map((c) => c.file),
          createdAt: Date.now(),
        })
        setSaveState('idle')
        setFailReason(null)
        createdMemoId.current = null
        clipsRef.current = []
        setClips([])
        setBody('')
        props.onSaved?.()
        return
      }
      setSaveState('failed')
      setFailReason({
        reason: error instanceof Error ? error.message : '',
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
      })
    }
  }

  /** 粘贴里带文件（截图、拖进剪贴板的文档）→ 直接进附件队列（R1-6）。 */
  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = event.clipboardData?.files
    if (files && files.length > 0) {
      event.preventDefault()
      pickFiles(files)
    }
  }

  /** 拖文件进输入框区域 → 同一条附件队列。 */
  function onDrop(event: DragEvent<HTMLElement>) {
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      pickFiles(files)
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return
    // 输入法组合期不提交：拼音选字时按 Enter 是选词（设计稿 §2 键盘行为）。
    if (event.nativeEvent.isComposing) return
    event.preventDefault()
    void commit()
  }

  function pickFiles(list: FileList | null) {
    if (!list) return
    for (const file of Array.from(list)) {
      const type = file.type || extensionMime(file.name)
      if (!CLIP_TYPES.has(type)) {
        setRejectNotice(`${file.name}：${t('memo.clip.reject.type')}`)
        continue
      }
      if (file.size > CLIP_MAX_BYTES) {
        setRejectNotice(`${file.name}：${t('memo.clip.reject.size')}`)
        continue
      }
      updateClips((prev) => [...prev, { kind: 'waiting', key: (crypto.randomUUID?.() ?? `clip-${Date.now()}-${Math.random().toString(36).slice(2)}`), file }])
    }
    // 同一个文件选两次也要触发 onChange：用完即复位
    if (fileInputRef.current) fileInputRef.current.value = ''
    // 正文那条已经建好的话（正在传附件），新选的直接跟着传——
    // 否则它会停在「等正文先存」，而正文早存完了，永远没人触发它
    if (createdMemoId.current) void uploadQueued(createdMemoId.current)
  }

  /** 摘掉一个还没收尾的附件行（已传上去的走详情里的移除）。 */
  function removeClip(key: string) {
    updateClips((prev) => prev.filter((c) => c.key !== key))
    finishIfSettled()
  }

  /** 重试一个失败的附件。正文那条早就存好了，直接往它身上再传。 */
  async function retryClip(clip: Extract<Clip, { kind: 'failed' }>) {
    const memoId = createdMemoId.current
    if (!memoId) return
    updateClips((prev) =>
      prev.map((c) =>
        c.key === clip.key ? { kind: 'uploading', key: c.key, file: c.file, percent: null } : c,
      ),
    )
    try {
      await uploadMemoAttachment({
        memoId,
        file: clip.file,
        onProgress: (percent) => {
          updateClips((prev) =>
            prev.map((c) =>
              c.key === clip.key && c.kind === 'uploading' ? { ...c, percent } : c,
            ),
          )
        },
      })
      updateClips((prev) =>
        prev.map((c) => (c.key === clip.key ? { kind: 'done', key: c.key, file: c.file } : c)),
      )
      finishIfSettled()
    } catch (error) {
      updateClips((prev) =>
        prev.map((c) =>
          c.key === clip.key
            ? {
                kind: 'failed',
                key: c.key,
                file: c.file,
                reason: error instanceof Error ? error.message : '',
              }
            : c,
        ),
      )
    }
  }

  function addLink() {
    const url = completeUrl(linkValue)
    if (!url) {
      setRejectNotice(t('memo.link.invalid'))
      return
    }
    // 设计稿 SEED 的链接条目就是「URL —— 备注」——链接是正文的一部分，
    // 列表按「链接」来源识别，不另存字段。
    setBody((prev) => (prev.trim() ? `${prev}\n${url} —— ` : `${url} —— `))
    setLinkValue('')
    setLinkOpen(false)
    inputRef.current?.focus()
  }

  const inProject = !!scope.projectId && !overrideMisc
  const scopeLine = inProject
    ? t('memo.scope.save.project', { project: scope.projectName ?? '' })
    : t('memo.scope.save.global')
  const scopeNote = inProject
    ? t('memo.scope.save.project.note')
    : t('memo.scope.save.global.note')

  const saveDisabled =
    !body.trim() ||
    saveState === 'saving' ||
    clipsBusy ||
    // 正文已存、附件在收尾：同一份文字不许存第二遍。
    (!!createdMemoId.current && clips.length > 0)
  // 正文存好之后这份文字就定稿了——附件收尾期间编辑会悄悄偏离已存的条目。
  const bodyLocked = !!createdMemoId.current && clips.length > 0

  return (
    <div
      data-testid={props['data-testid'] ?? 'memo-capture'}
      onDrop={onDrop}
      onDragOver={(event) => event.preventDefault()}
      className="overflow-hidden rounded-npc-card border border-m-line2 bg-m-bg1 shadow-npc-card"
    >
      {/* 作用域行：硬约束 01，一直可见 */}
      <div className="flex h-[38px] items-center gap-[8px] border-b border-m-line1 bg-m-bg2 px-[12px]">
        {inProject ? (
          <Folder size={13} strokeWidth={1.8} className="shrink-0 text-m-t1" aria-hidden />
        ) : (
          <Globe size={13} strokeWidth={1.8} className="shrink-0 text-m-t2" aria-hidden />
        )}
        <span
          className="truncate text-[12.5px] font-medium text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          data-testid="memo-capture-scope"
        >
          {scopeLine}
        </span>
        <span className="hidden truncate text-[12px] text-m-t4 min-[700px]:inline"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {scopeNote}
        </span>
        {scope.projectId ? (
          <button
            type="button"
            onClick={() => {
              // 切作用域 = 换一张草稿纸：当前内容存回原作用域，
              // 换成新作用域存过的那份。之前是直接换 key，下一次按键
              // 就把另一边存过的草稿悄悄覆盖掉。
              try {
                const nextOverride = !overrideMisc
                const nextKey = `memo-draft:${nextOverride ? 'global' : (scope.projectId ?? 'global')}`
                if (body.trim()) window.localStorage.setItem(draftKey, body)
                else window.localStorage.removeItem(draftKey)
                setBody(window.localStorage.getItem(nextKey) ?? '')
              } catch {
                // localStorage 不可用就只切作用域，不搬草稿
              }
              setOverrideMisc((v) => !v)
            }}
            className="ml-auto h-[24px] shrink-0 cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[9px] font-[inherit] text-[11px] text-m-t3 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            data-testid="memo-capture-scope-switch"
          >
            {overrideMisc ? t('memo.list.this.project') : t('memo.scope.switch.misc')}
          </button>
        ) : null}
      </div>

      <div className="px-[12px] pt-[12px]">
        <textarea
          ref={inputRef}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          disabled={bodyLocked}
          aria-label={scopeLine}
          placeholder={props.placeholder ?? t('memo.empty.global.placeholder')}
          data-testid="memo-capture-input"
          className={cn(
            'w-full resize-none border-none bg-transparent p-0 font-[inherit] text-[18px] leading-[1.75] text-m-t1 outline-none placeholder:text-m-t4',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            floating ? 'min-h-[56px]' : 'min-h-[44px]',
          )}
        />
      </div>

      {/* 链接行（工具行「链接」展开）：把网址写进正文 */}
      {linkOpen ? (
        <div className="mx-[12px] mt-[8px] flex flex-wrap items-center gap-[7px]" data-testid="memo-link-row">
          <Link2 size={13} strokeWidth={1.8} className="shrink-0 text-m-t4" aria-hidden />
          <input
            value={linkValue}
            onChange={(event) => setLinkValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault()
                addLink()
              }
            }}
            placeholder={t('memo.link.placeholder')}
            aria-label={t('memo.link.placeholder')}
            data-testid="memo-link-input"
            className="h-[28px] min-w-[180px] flex-1 rounded-[7px] border border-m-line2 bg-m-bg1 px-[9px] font-[inherit] text-[13px] text-m-t1 outline-none placeholder:text-m-t4"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          />
          <button
            type="button"
            onClick={addLink}
            data-testid="memo-link-add"
            className="h-[28px] shrink-0 cursor-pointer rounded-[7px] border-none bg-m-pri px-[11px] font-[inherit] text-[12px] font-medium text-m-pri-t"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            <T id="memo.link.add" />
          </button>
        </div>
      ) : null}

      {/* 附件条：选中的文件各占一行，状态各自算 */}
      {clips.length > 0 ? (
        <ul className="m-0 mt-[8px] flex list-none flex-col gap-[5px] px-[12px]" data-testid="memo-clips">
          {clips.map((clip) => (
            <li
              key={clip.key}
              data-testid="memo-clip"
              className="flex min-w-0 flex-wrap items-center gap-x-[8px] gap-y-[3px] rounded-[7px] border border-m-line1 bg-m-bg2 px-[9px] py-[6px]"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            >
              <ClipIcon mime={clip.file.type || extensionMime(clip.file.name)} />
              <span className="min-w-0 flex-1 truncate text-[15px] text-m-t1" title={clip.file.name}> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                {clip.file.name}
              </span>
              <span className="shrink-0 font-npc-mono text-[10.5px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                {humanBytes(clip.file.size)}
              </span>
              {clip.kind === 'waiting' ? (
                <span className="shrink-0 text-[11px] text-m-t3" data-testid="memo-clip-waiting"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  <T id="memo.clip.state.waiting" />
                </span>
              ) : clip.kind === 'uploading' ? (
                <span
                  className="shrink-0 font-npc-mono text-[11px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  data-testid="memo-clip-uploading"
                >
                  {clip.percent === null
                    ? t('memo.capture.saving')
                    : t('memo.clip.state.uploading', { percent: String(clip.percent) })}
                </span>
              ) : clip.kind === 'failed' ? (
                <span className="flex shrink-0 items-center gap-[7px]" data-testid="memo-clip-failed">
                  <span className="text-[11px] text-m-bad-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                    {t('memo.clip.state.failed')}
                    {clip.reason ? `（${clip.reason}）` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => void retryClip(clip)}
                    className="h-[24px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[8px] font-[inherit] text-[11px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  >
                    <T id="memo.capture.retry" />
                  </button>
                </span>
              ) : (
                <span className="shrink-0 text-[11px] text-m-ok-t" data-testid="memo-clip-done"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  <T id="memo.clip.state.done" />
                </span>
              )}
              <button
                type="button"
                aria-label={t('memo.clip.remove')}
                onClick={() => removeClip(clip.key)}
                className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-[11px] text-m-t4 hover:text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                data-testid="memo-clip-remove"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-[7px] px-[12px] pb-[12px] pt-[11px]">
        <VoiceEntry open={voiceOpen} setOpen={setVoiceOpen} onSaved={props.onSaved} scope={effectiveScope} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          title={t('memo.capture.tool.clip')}
          data-testid="memo-clip-open"
          className="flex h-[34px] cursor-pointer items-center gap-[6px] rounded-[8px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[13.5px] text-m-t3 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        >
          <Paperclip size={12} strokeWidth={1.9} aria-hidden />
          <T id="memo.capture.tool.clip" />
        </button>
        <button
          type="button"
          onClick={() => setLinkOpen((v) => !v)}
          title={t('memo.capture.tool.link')}
          data-testid="memo-link-open"
          className={cn(
            'flex h-[28px] cursor-pointer items-center gap-[6px] rounded-[7px] border px-[10px] font-[inherit] text-[11.5px]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            linkOpen
              ? 'border-m-line3 bg-m-bg3 text-m-t1'
              : 'border-m-line2 bg-m-bg1 text-m-t3 hover:text-m-t1',
          )}
        >
          <Link2 size={12} strokeWidth={1.9} aria-hidden />
          <T id="memo.capture.tool.link" />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={CLIP_ACCEPT}
          className="hidden"
          onChange={(event) => pickFiles(event.target.files)}
          data-testid="memo-clip-input"
          aria-hidden
          tabIndex={-1}
        />

        <span className="ml-auto hidden font-npc-mono text-[14px] text-m-t4 min-[860px]:inline"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {t('memo.capture.hint')}
        </span>
        <button
          type="button"
          disabled={saveDisabled}
          onClick={() => void commit()}
          data-testid="memo-capture-save"
          className={cn(
            'h-[36px] cursor-pointer rounded-[8px] border-none px-[16px] font-[inherit] text-[15px] font-medium',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            saveDisabled
              ? 'cursor-not-allowed bg-m-bg3 text-m-t4'
              : 'bg-m-pri text-m-pri-t',
          )}
        >
          <T
            id={
              saveState === 'failed'
                ? 'memo.capture.retry'
                : saveState === 'saving'
                  ? 'memo.capture.saving'
                  : 'memo.capture.save'
            }
          />
        </button>
      </div>

      {/* 失败状态条（S15 / SPEC-components §8）：出了什么事 · 东西在不在 · 该做什么 */}
      {saveState === 'failed' && failReason ? (
        <div
          role="alert"
          data-testid="memo-capture-failed"
          className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px] border-t border-m-line1 bg-m-bad-s px-[12px] py-[9px]"
        >
          <span className="text-[14.5px] text-m-bad-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            {t('memo.save.fail.foot', { reason: failReason.reason, time: failReason.time })}
          </span>
          <button
            type="button"
            onClick={() => void commit()}
            className="ml-auto h-[26px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[11.5px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            data-testid="memo-capture-retry"
          >
            <T id="memo.capture.retry" />
          </button>
        </div>
      ) : null}

      {/* 附件没都上去：正文已存好（列表里看得到），失败的留着重试/移除 */}
      {clipsFailed.length > 0 && saveState !== 'failed' ? (
        <div
          role="alert"
          data-testid="memo-clips-failed"
          className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px] border-t border-m-line1 bg-m-bad-s px-[12px] py-[9px]"
        >
          <span className="text-[14.5px] text-m-bad-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            {t('memo.clip.fail.foot', {
              names: clipsFailed.map((c) => c.file.name).join('、'),
            })}
          </span>
        </div>
      ) : null}

      {/* 类型 / 大小拦截（上传前，SPEC-components §8） */}
      {rejectNotice ? (
        <div
          role="alert"
          data-testid="memo-clip-reject"
          className="flex items-center gap-[10px] border-t border-m-line1 bg-m-wn-s px-[12px] py-[9px]"
        >
          <span className="min-w-0 flex-1 text-[14.5px] text-m-wn-t">{rejectNotice}</span> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <button
            type="button"
            onClick={() => setRejectNotice(null)}
            className="h-[26px] shrink-0 cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[11.5px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            <T id="ui.close" />
          </button>
        </div>
      ) : null}
    </div>
  )
})

/**
 * 语音入口（M3-60 的状态机原样保留，壳按设计稿换）。
 *
 * 录音态只显示**真实秒数**与有没有收到声音——不画波形（波形是
 * 装饰当数据）。语音路径强制选可见性：没选之前存不了，文字路径
 * 可以「都没标」，语音路径服务端同样拒绝（ErrVoiceNeedsAccess）。
 */
function VoiceEntry(props: {
  open: boolean
  setOpen: (open: boolean) => void
  onSaved?: () => void
  /** 作用域行显示存到哪儿，语音就必须存到哪儿（硬约束 01）。 */
  scope: MemoScope
}) {
  const open = props.open
  const voiceMemo = useCreateVoiceMemo()
  const [phase, setPhase] = useState<VoicePhase>({ kind: 'idle' })
  // 试听地址（R2 巡检修复）：一个 blob 只建一个 objectURL，换段/卸载时 revoke——
  // 之前写在 render 里，每重渲染漏一个
  const [auditionUrl, setAuditionUrl] = useState<string | null>(null)
  const phaseBlob = phase.kind === 'recorded' || phase.kind === 'saving' ? phase.blob : null
  useEffect(() => {
    if (!phaseBlob) {
      setAuditionUrl(null)
      return
    }
    const url = URL.createObjectURL(phaseBlob)
    setAuditionUrl(url)
    return () => {
      URL.revokeObjectURL(url)
      setAuditionUrl(null)
    }
  }, [phaseBlob])
  const [voiceAccess, setVoiceAccess] = useState<'default' | 'shared' | 'hidden' | null>(null)
  const [elapsedMS, setElapsedMS] = useState(0)
  const [hasLevel, setHasLevel] = useState(false)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const timerRef = useRef<number | null>(null)
  const startedAtRef = useRef(0)

  const recordingSupported =
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia

  const cleanupMedia = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    void audioCtxRef.current?.close().catch(() => {})
    audioCtxRef.current = null
    recorderRef.current = null
  }, [])

  // 卸载时收摊：麦克风是一条硬件通道，开着不放是会被人注意到的。
  useEffect(() => cleanupMedia, [cleanupMedia])

  async function startRecording() {
    setHasLevel(false)
    if (!recordingSupported) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = pickMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        const durationMS = Math.max(1, Date.now() - startedAtRef.current)
        cleanupMedia()
        if (blob.size === 0) {
          setPhase({ kind: 'idle' })
          return
        }
        setPhase({ kind: 'recorded', blob, mimeType: type, durationMS })
      }
      recorderRef.current = recorder
      startedAtRef.current = Date.now()
      setElapsedMS(0)
      recorder.start(250)
      setPhase({ kind: 'recording' })
      timerRef.current = window.setInterval(() => {
        setElapsedMS(Date.now() - startedAtRef.current)
      }, 250)

      // 电平探测：只回答「有没有收到声音」，不算音量、不画波形
      try {
        const ctx = new AudioContext()
        audioCtxRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        source.connect(analyser)
        const buf = new Uint8Array(analyser.fftSize)
        const probe = () => {
          if (!audioCtxRef.current) return
          analyser.getByteTimeDomainData(buf)
          for (const v of buf) {
            if (Math.abs(v - 128) > 4) {
              setHasLevel(true)
              break
            }
          }
          window.requestAnimationFrame(probe)
        }
        window.requestAnimationFrame(probe)
      } catch {
        // 电平探测拿不到就不显示——它是个提示，不是功能
      }
    } catch {
      cleanupMedia()
      setPhase({ kind: 'no-mic' })
    }
  }

  function stopRecording() {
    recorderRef.current?.stop()
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  function saveVoice() {
    if (phase.kind !== 'recorded' || !voiceAccess) return
    setPhase({
      kind: 'saving',
      blob: phase.blob,
      mimeType: phase.mimeType,
      durationMS: phase.durationMS,
    })
    voiceMemo.mutate(
      {
        blob: phase.blob,
        mimeType: phase.mimeType,
        durationMS: phase.durationMS,
        aiAccess: voiceAccess,
        projectId: props.scope.projectId,
      },
      {
        onSuccess: () => {
          setPhase({ kind: 'idle' })
          setVoiceAccess(null)
          props.setOpen(false)
          props.onSaved?.()
        },
        onError: () => {
          setPhase({
            kind: 'recorded',
            blob: phase.blob,
            mimeType: phase.mimeType,
            durationMS: phase.durationMS,
          })
        },
      },
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => props.setOpen(true)}
        data-testid="memo-voice-open"
        className="flex h-[34px] cursor-pointer items-center gap-[6px] rounded-[8px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[13.5px] text-m-t3 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
      >
        <Mic size={12} strokeWidth={1.9} aria-hidden />
        <T id="memo.capture.tool.voice" />
      </button>
    )
  }

  if (!recordingSupported) {
    return (
      <span className="text-[11.5px] text-m-t3" data-testid="memo-voice-unsupported"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
        <T id="memo.voice.unsupported" />
      </span>
    )
  }

  if (phase.kind === 'recording') {
    return (
      <div className="flex flex-wrap items-center gap-[8px]" data-testid="memo-voice-recording">
        <span className="font-npc-mono text-[16px] text-m-t1" aria-live="polite"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {clock(elapsedMS)}
        </span>
        <span className={cn('text-[11.5px]', hasLevel ? 'text-m-ok-t' : 'text-m-wn-t')}> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <T id={hasLevel ? 'memo.voice.level.on' : 'memo.voice.level.off'} />
        </span>
        <button
          type="button"
          onClick={stopRecording}
          className="h-[28px] cursor-pointer rounded-[7px] border-none bg-m-pri px-[12px] font-[inherit] text-[12px] font-medium text-m-pri-t"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        >
          <T id="memo.voice.stop" />
        </button>
      </div>
    )
  }

  if (phase.kind === 'recorded' || phase.kind === 'saving') {
    const saving = phase.kind === 'saving'
    return (
      <div className="flex flex-wrap items-center gap-[8px]" data-testid="memo-voice-review">
        <span className="font-npc-mono text-[11.5px] text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {clock(phase.durationMS)}
        </span>
        {/* 试听（R2-3）：本机 blob，不上传就能听一遍再决定 */}
        <audio
          controls
          preload="metadata"
          src={auditionUrl ?? undefined}
          aria-label={t('memo.voice.audition')}
          data-testid="memo-voice-audition"
          className="h-[32px]"
        />
        <select
          value={voiceAccess ?? ''}
          onChange={(event) => setVoiceAccess(event.target.value as 'default' | 'shared' | 'hidden')}
          disabled={saving}
          aria-label={t('memo.access.label')}
          data-testid="memo-voice-access"
          className={cn(
            'h-[32px] cursor-pointer rounded-[7px] border bg-m-bg1 px-[10px] font-[inherit] text-[14.5px]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            voiceAccess
              ? 'border-m-line2 text-m-t2'
              : 'border-m-wn-t/40 text-m-wn-t',
          )}
        >
          <option value="" disabled>
            {t('memo.access.label')}
          </option>
          <option value="default">{t('memo.ai.default')}</option>
          <option value="shared">{t('memo.ai.shared')}</option>
          <option value="hidden">{t('memo.ai.hidden')}</option>
        </select>
        <button
          type="button"
          onClick={saveVoice}
          disabled={saving || !voiceAccess}
          data-testid="memo-voice-save"
          className={cn(
            'h-[28px] cursor-pointer rounded-[6px] border-none px-[12px] font-[inherit] text-[12px] font-medium',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            saving || !voiceAccess
              ? 'cursor-not-allowed bg-m-bg3 text-m-t4'
              : 'bg-m-pri text-m-pri-t',
          )}
        >
          <T id={saving ? 'memo.voice.saving' : 'memo.voice.save'} />
        </button>
        <button
          type="button"
          onClick={() => {
            setPhase({ kind: 'idle' })
            setVoiceAccess(null)
            props.setOpen(false)
          }}
          className="h-[28px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[10px] font-[inherit] text-[11.5px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        >
          <T id="memo.voice.rerecord" />
        </button>
      </div>
    )
  }

  if (phase.kind === 'no-mic') {
    return (
      <div className="flex items-center gap-[8px]" data-testid="memo-voice-nomic">
        <span className="text-[14.5px] text-m-wn-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <T id="memo.voice.no_mic" />
        </span>
        <button
          type="button"
          onClick={() => void startRecording()}
          className="h-[28px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[10px] font-[inherit] text-[11.5px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        >
          <T id="memo.capture.tool.voice" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-[8px]" data-testid="memo-voice-entry">
      <button
        type="button"
        onClick={() => void startRecording()}
        data-testid="memo-voice-record"
        className="h-[28px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[10px] font-[inherit] text-[11.5px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
      >
        <T id="memo.capture.tool.voice" />
      </button>
      <button
        type="button"
        onClick={() => props.setOpen(false)}
        className="h-[28px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[10px] font-[inherit] text-[11.5px] text-m-t3"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
      >
        <T id="memo.capture.tool.voice.cancel" />
      </button>
    </div>
  )
}

/** jsdom 里没有 MediaRecorder，落在 unsupported。 */
type VoicePhase =
  | { kind: 'idle' }
  | { kind: 'no-mic' }
  | { kind: 'recording' }
  | { kind: 'recorded'; blob: Blob; mimeType: string; durationMS: number }
  | { kind: 'saving'; blob: Blob; mimeType: string; durationMS: number }

/** 挑一个这台浏览器真能录的容器。Safari 不认 webm，出 mp4。 */
function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

/**
 * 链接自动补齐（用户需求）：粘贴的网址常常不带协议头——
 * `www.foo.com/x`、`foo.com` 都按 https:// 补上再存；带协议的
 * （http/https/ftp 等 xxx:// 形态）原样保留。看着不像网址的返回空，
 * 由调用方出那句人话。
 */
export function completeUrl(raw: string): string {
  const value = raw.trim()
  if (!value || /\s/.test(value)) return ''
  // 已带协议（https、http、ftp…）——不动用户的
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return value
  // 裸域名/路径：至少一个点、以域名字符开头（www.foo.com、foo.com/x?a=1）
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?$/i.test(value)) return `https://${value}`
  return ''
}

/** `0:07`。毫秒转分秒是格式不是句子。 */
function clock(ms: number): string {
  const total = Math.floor(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** 有些系统选文件不给 MIME（file.type 空），按扩展名认一遍同一张白名单。 */
function extensionMime(name: string): string {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase()
  const map: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    gif: 'image/gif',
    pdf: 'application/pdf',
    txt: 'text/plain',
    md: 'text/markdown',
    csv: 'text/csv',
    json: 'application/json',
    zip: 'application/zip',
  }
  return map[ext] ?? ''
}

/** `1.2MB` / `340KB`。给附件行用的人话大小。 */
export function humanBytes(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)}MB`
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))}KB`
  return `${bytes}B`
}

/** 附件行的文件图标：图片给图片图标，其余给文档图标。 */
function ClipIcon(props: { mime: string }) {
  const Icon = props.mime.startsWith('image/') ? ImageIcon : FileText
  return <Icon size={12} strokeWidth={1.8} className="shrink-0 text-m-t4" aria-hidden />
}

/** 提示行的拆词渲染：Enter / Shift+Enter 两个 kbd 小方块（样式，不是文案改动）。 */
export function CaptureKeyHint() {
  const parts = t('memo.capture.hint').split(/(\bEnter\b|\bShift\+Enter\b)/)
  return (
    <>
      {parts.map((part, index) =>
        part === 'Enter' || part === 'Shift+Enter' ? (
          <kbd
            key={index}
            className="mx-[2px] rounded-npc-inline border border-m-line2 bg-m-bg2 px-[5px] py-[1px] font-npc-mono text-npc-meta not-italic text-m-t2"
          >
            {part}
          </kbd>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  )
}
